import React, { useMemo, useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Calendar,
  FileText,
  IndianRupee,
  Pencil,
  Plus,
  Save,
  Search,
  Tag,
  Trash2,
  X,
} from "lucide-react";
import { MenuContainer, ThreeDotsButton, Dot, DropdownMenu, MenuItem, ItemIconContainer, ItemLabel, Backdrop, ToolbarLeft, ToolbarRight } from "./documentSharedStyles";
import ExportDropdown from "../../atoms/Dashboard/ExportDropdown";
import { SubmitButton, FormLabelIcon, FormGradientDefs } from "../../molecules/Dashboard/Member/memberFormStyles";
import {
  usePortaledActionMenu,
  PageContainer,
  Hero,
  HeroGlow,
  HeroContent,
  HeroLeft,
  BackChip,
  HeroText,
  HeroBadge,
  PageTitle,
  PageSubtitle,
  Panel,
  Toolbar,
  SearchControl,
  ClearButton,
  EntriesControl,
  FooterRow,
  FooterText,
  Pagination,
  PageButton,
  ModalOverlay,
  ModalShell,
  ModalHero,
  ModalHeroGlow,
  ModalHeroRow,
  ModalHeroText,
  ModalHeroBadge,
  ModalTitle,
  ModalSubtitle,
  CloseBtn,
  ModalBody,
  FormCard,
  FieldGroup,
  FieldLabel,
  FieldInput,
  FieldTextArea,
  FieldSelect,
  ModalActions,
  SecondaryButton,
  PrimaryButton,
} from "./quotationMasterListShared";
import { showSuccessToast, showErrorToast } from "../../atoms/Utlis/Toast";
import { copyDataToClipboard, exportDataToCSV, exportDataToExcel } from "../../../Utils/exportHelpers";
import { exportElementAsPDF } from "../../../Utils/pdfHelpers";
import { formatINR } from "../../../Utils/helper";
import { useAppSelector } from "../../../Redux/Hooks";
import PermissionGate from "../../atoms/Utlis/PermissionGate";
import { usePermissions } from "../../../hooks/usePermissions";
import { ListReportPrintTemplate, CellBold } from "./ListReportPrintTemplate";
import {
  getEventTypesAPI,
  createEventTypeAPI,
  updateEventTypeAPI,
  deleteEventTypeAPI,
} from "../../../Redux/ApiCalls/Dashboard/QuotationAPI";
import {
  PanelTableScroll,
  TableWrap,
  PaymentsTable,
  AmountCell,
} from "../../atoms/Dashboard/CustomerTransaction/transactionTableStyles";
import { getApiErrorMessage } from "../../atoms/Utlis/getApiErrorMessage";
import DeletePopup from "../../atoms/Dashboard/Folder/DeletePopup";

const ScrollPage = styled(PageContainer)`
  height: auto;
  min-height: 100%;
  overflow: visible;
  flex-shrink: 0;
`;

const ScrollPanel = styled(Panel)`
  flex: none;
  min-height: unset;
  overflow: visible;
`;

const ScrollTable = styled(PanelTableScroll)`
  flex: none;
  overflow-y: visible;
  min-height: unset;
`;



type EventType = {
  id: number;
  name: string;
  description: string;
  price?: number;
  created_at: string;
  branch_id?: number | null;
  branch_name?: string | null;
};

const QuotationEventTypesPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, branches, selectedBranch } = useAppSelector((state) => state.auth);
  const { hasPermission } = usePermissions();
  const canUpdate = hasPermission("quotation.update");
  const canDelete = hasPermission("quotation.delete");

  const reportPrintRef = useRef<HTMLDivElement>(null);
  const [eventTypesList, setEventTypesList] = useState<EventType[]>([]);
  const [loading, setLoading] = useState(false);

  const [searchVal, setSearchVal] = useState("");
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchText(searchVal);
      setCurrentPage(1);
    }, 800);
    return () => clearTimeout(handler);
  }, [searchVal]);

  const [entries, setEntries] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingEventType, setEditingEventType] = useState<EventType | null>(null);
  const {
    activeMenuId,
    menuCoords,
    menuButtonRefs,
    scrollRef,
    handleToggleMenu,
    closeMenu,
  } = usePortaledActionMenu(140);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [branchId, setBranchId] = useState<string | number>(selectedBranch?.id || "");
  const [eventTypeName, setEventTypeName] = useState("");
  const [description, setDescription] = useState("");
  const [eventTypePrice, setEventTypePrice] = useState<number | string>("");

  const [totalCount, setTotalCount] = useState(0);

  const fetchEventTypes = async () => {
    setLoading(true);
    try {
      const res = await getEventTypesAPI({
        page: currentPage,
        page_size: entries,
        search: searchText,
        branch_id: selectedBranch?.id,
      });
      setEventTypesList(res?.results || []);
      setTotalCount(res?.count || 0);
    } catch (err: any) {
      showErrorToast(getApiErrorMessage(err, "Failed to fetch event types"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEventTypes();
  }, [currentPage, entries, searchText, selectedBranch?.id]);

  const openAddModal = () => {
    setEditingEventType(null);
    setBranchId(selectedBranch?.id || "");
    setEventTypeName("");
    setDescription("");
    setEventTypePrice("");
    setIsAddModalOpen(true);
  };

  const openEditModal = (eventType: EventType) => {
    setEditingEventType(eventType);
    setBranchId(eventType.branch_id || "");
    setEventTypeName(eventType.name);
    setDescription(eventType.description || "");
    setEventTypePrice(eventType.price !== undefined && eventType.price !== null ? eventType.price : "");
    setIsAddModalOpen(true);
  };

  const handleSaveEventType = async () => {
    if (isSaving) return;
    if (!eventTypeName.trim()) {
      showErrorToast("Event type name is required");
      return;
    }
    setIsSaving(true);
    const data = {
      name: eventTypeName,
      description,
      price: eventTypePrice !== "" ? Number(eventTypePrice) : null,
      branch_id: branchId ? Number(branchId) : null,
    };

    try {
      if (editingEventType) {
        await updateEventTypeAPI(editingEventType.id, data);
        showSuccessToast("Event type updated successfully!");
      } else {
        await createEventTypeAPI(data);
        showSuccessToast("Event type created successfully!");
      }
      setIsAddModalOpen(false);
      fetchEventTypes();
    } catch (err: any) {
      showErrorToast(getApiErrorMessage(err, "Failed to save event type"));
    } finally {
      setIsSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (deleteConfirmId === null || isDeleting) return;
    setIsDeleting(true);
    try {
      await deleteEventTypeAPI(deleteConfirmId);
      showSuccessToast("Event type deleted successfully!");
      fetchEventTypes();
    } catch (err: any) {
      showErrorToast(getApiErrorMessage(err, "Failed to delete event type"));
    } finally {
      setIsDeleting(false);
      setDeleteConfirmId(null);
    }
  };

  const handleCopy = () => {
    copyDataToClipboard(
      eventTypesList,
      ["ID", "Name", "Description"],
      ["id", "name", "description"],
      "Event types list copied to clipboard"
    );
  };

  const handleExportCSV = () => {
    exportDataToCSV(
      eventTypesList,
      ["ID", "Name", "Description", "Created At"],
      ["id", "name", "description", "created_at"],
      "event_types_export.csv"
    );
  };

  const handleExportExcel = () => {
    exportDataToExcel(
      eventTypesList,
      ["ID", "Name", "Description", "Created At"],
      ["id", "name", "description", "created_at"],
      "event_types_export.xls"
    );
  };

  const eventTypeReportRows = useMemo(
    () =>
      eventTypesList.map((e) => ({
        name: e.name,
        description: e.description || "—",
        price:
          e.price !== undefined && e.price !== null
            ? `₹${formatINR(e.price)}`
            : "—",
        price_raw: Number(e.price) || 0,
        created_at: e.created_at
          ? new Date(e.created_at).toLocaleDateString("en-IN", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })
          : "—",
      })),
    [eventTypesList]
  );

  const eventTypeReportSummary = useMemo(() => {
    const totalValue = eventTypeReportRows.reduce((sum, row) => sum + row.price_raw, 0);
    return [
      { label: "Total Event Types", value: String(eventTypeReportRows.length) },
      { label: "Total Price Value", value: `₹${formatINR(totalValue)}`, highlight: true },
    ];
  }, [eventTypeReportRows]);

  const handleExportPDF = async () => {
    try {
      await new Promise((resolve) => setTimeout(resolve, 300));

      if (!reportPrintRef.current) {
        showErrorToast("Report template not ready");
        return;
      }

      const blob = await exportElementAsPDF(reportPrintRef.current);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "event_types_report.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      showSuccessToast("PDF exported successfully");
    } catch (err) {
      showErrorToast("Failed to generate PDF");
    }
  };

  const visibleEventTypes = eventTypesList;
  const totalFilteredCount = totalCount;
  const totalPages = Math.ceil(totalFilteredCount / entries) || 1;

  const activeMenuEventType = useMemo(
    () => visibleEventTypes.find((eventType) => eventType.id === activeMenuId) ?? null,
    [visibleEventTypes, activeMenuId]
  );

  return (
    <ScrollPage>
      {isAddModalOpen &&
        createPortal(
          <>
            <FormGradientDefs />
            <ModalOverlay onClick={() => setIsAddModalOpen(false)}>
              <ModalShell style={{ maxWidth: 520 }} onClick={(event) => event.stopPropagation()}>
                <ModalHero>
                  <ModalHeroGlow />
                  <ModalHeroRow>
                    <ModalHeroText>
                      <ModalHeroBadge>
                        {editingEventType ? <Pencil size={11} /> : <Plus size={11} />}
                        {editingEventType ? "Edit event type" : "New event type"}
                      </ModalHeroBadge>
                      <ModalTitle>{editingEventType ? "Edit Event Type" : "Add Event Type"}</ModalTitle>
                      <ModalSubtitle>
                        {editingEventType ? "Update event type details." : "Create static quotation event type entry."}
                      </ModalSubtitle>
                    </ModalHeroText>
                    <CloseBtn type="button" onClick={() => setIsAddModalOpen(false)} aria-label="Close">
                      <X size={18} />
                    </CloseBtn>
                  </ModalHeroRow>
                </ModalHero>

                <ModalBody>
                  <FormCard>
                    {branches && branches.length > 0 && (
                      <FieldGroup>
                        <FieldLabel>
                          Branch (Optional)
                        </FieldLabel>
                        <FieldSelect value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                          <option value="">All Branches / Main Studio (HQ)</option>
                          {branches.map((b) => (
                            <option key={b.id} value={b.id}>
                              {b.branch_name} {b.city ? `(${b.city})` : ''}
                            </option>
                          ))}
                        </FieldSelect>
                      </FieldGroup>
                    )}
                    <FieldGroup>
                      <FieldLabel>
                        <FormLabelIcon icon={Tag} />
                        Event Type Name
                      </FieldLabel>
                      <FieldInput
                        placeholder="Enter event type name"
                        value={eventTypeName}
                        onChange={(e) => setEventTypeName(e.target.value)}
                      />
                    </FieldGroup>
                    <FieldGroup>
                      <FieldLabel>
                        <FormLabelIcon icon={FileText} />
                        Description
                      </FieldLabel>
                      <FieldTextArea
                        placeholder="Enter description"
                        rows={4}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                      />
                    </FieldGroup>
                    <FieldGroup>
                      <FieldLabel>
                        <FormLabelIcon icon={IndianRupee} />
                        Price (₹)
                      </FieldLabel>
                      <FieldInput
                        type="number"
                        placeholder="Enter price"
                        value={eventTypePrice}
                        onChange={(e) => setEventTypePrice(e.target.value)}
                      />
                    </FieldGroup>
                  </FormCard>
                </ModalBody>

                <ModalActions>
                  <PrimaryButton type="button" onClick={handleSaveEventType} disabled={isSaving}>
                    <Save size={16} />
                    {isSaving ? "Saving..." : editingEventType ? "Update Event Type" : "Save Event Type"}
                  </PrimaryButton>
                  <SecondaryButton type="button" onClick={() => setIsAddModalOpen(false)}>
                    Cancel
                  </SecondaryButton>
                </ModalActions>
              </ModalShell>
            </ModalOverlay>
          </>,
          document.body
        )}

      <Hero>
        <HeroGlow />
        <HeroContent>
          <HeroLeft>
            <BackChip onClick={() => navigate("/dashboard/quotation/")} aria-label="Go back">
              <ArrowLeft size={18} />
            </BackChip>
            <HeroText>
              <HeroBadge>
                <Calendar size={13} />
                Quotation masters
              </HeroBadge>
              <PageTitle className="linear-text">All Event Types</PageTitle>
              <PageSubtitle>Manage event type masters used in quotations.</PageSubtitle>
            </HeroText>
          </HeroLeft>
          <PermissionGate permission="quotation.create">
            <SubmitButton
              type="button"
              $ready
              onClick={openAddModal}
              style={{ minWidth: 180, height: 46 }}
            >
              <Plus size={16} /> Add Event Type
            </SubmitButton>
          </PermissionGate>
        </HeroContent>
      </Hero>

      <ScrollPanel>
        <Toolbar>
          <ToolbarLeft>
            <SearchControl>
              <Search size={16} />
              <input
                value={searchVal}
                onChange={(event) => setSearchVal(event.target.value)}
                placeholder="Search event types"
              />
              {searchVal && (
                <ClearButton
                  type="button"
                  onClick={() => {
                    setSearchVal("");
                    setSearchText("");
                    setCurrentPage(1);
                  }}
                >
                  <X size={14} />
                </ClearButton>
              )}
            </SearchControl>

            <EntriesControl>
              <span>Show</span>
              <select value={entries} onChange={(event) => { setEntries(Number(event.target.value)); setCurrentPage(1); }}>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
              <span>entries</span>
            </EntriesControl>
          </ToolbarLeft>

          <ToolbarRight>
            <ExportDropdown
              onCopy={handleCopy}
              onCSV={handleExportCSV}
              onExcel={handleExportExcel}
              onPDF={handleExportPDF}
              onPrint={() => window.print()}
            />
          </ToolbarRight>
        </Toolbar>


        <ScrollTable ref={scrollRef}>
          <TableWrap>
            <PaymentsTable $minWidth={860}>
            <thead>
              <tr>
                <th><span>Event Type Name</span></th>
                <th><span>Branch</span></th>
                <th><span>Description</span></th>
                <th><span>Price (₹)</span></th>
                <th><span>Created Date</span></th>
                <th><span>Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} style={{ textAlign: "center", padding: "30px" }}>
                    Loading event types...
                  </td>
                </tr>
              ) : visibleEventTypes.length > 0 ? (
                visibleEventTypes.map((eventType, idx) => (
                  <tr key={eventType.id}>
                    <td>{eventType.name}</td>
                    <td>
                      {eventType.branch_name ? (
                        <span style={{ fontSize: 11, background: '#e0e7ff', color: '#4338ca', padding: '2px 8px', borderRadius: 999, fontWeight: 600 }}>
                          {eventType.branch_name}
                        </span>
                      ) : (
                        <span style={{ fontSize: 11, color: '#94a3b8' }}>-</span>
                      )}
                    </td>
                    <td>{eventType.description || "-"}</td>
                    <td>
                      {eventType.price !== undefined && eventType.price !== null ? (
                        <AmountCell>₹{eventType.price}</AmountCell>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td>{eventType.created_at ? new Date(eventType.created_at).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }) : "-"}</td>
                    <td>
                      {(canUpdate || canDelete) && (
                        <MenuContainer>
                          <ThreeDotsButton
                            ref={(el) => { menuButtonRefs.current[eventType.id] = el; }}
                            type="button"
                            aria-expanded={activeMenuId === eventType.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleMenu(eventType.id);
                            }}
                          >
                            <Dot />
                            <Dot />
                            <Dot />
                          </ThreeDotsButton>
                        </MenuContainer>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} style={{ textAlign: "center", padding: "30px" }}>
                    No event types found.
                  </td>
                </tr>
              )}
            </tbody>
            </PaymentsTable>
          </TableWrap>
        </ScrollTable>

        <FooterRow>
          <FooterText>
            Showing {totalFilteredCount === 0 ? 0 : (currentPage - 1) * entries + 1} to{" "}
            {Math.min(currentPage * entries, totalFilteredCount)} of {totalFilteredCount} entries
          </FooterText>
          <Pagination>
            <PageButton
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((prev) => prev - 1)}
            >
              Previous
            </PageButton>
            {Array.from({ length: totalPages }).map((_, idx) => (
              <PageButton
                key={idx}
                $active={currentPage === idx + 1}
                onClick={() => setCurrentPage(idx + 1)}
              >
                {idx + 1}
              </PageButton>
            ))}
            <PageButton
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((prev) => prev + 1)}
            >
              Next
            </PageButton>
          </Pagination>
        </FooterRow>
      </ScrollPanel>

      {activeMenuEventType && menuCoords &&
        createPortal(
          <>
            <Backdrop onClick={closeMenu} />
            <DropdownMenu
              $fixed
              style={{
                top: menuCoords.top,
                bottom: menuCoords.bottom,
                right: menuCoords.right,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {canUpdate && (
                <MenuItem
                  type="button"
                  onClick={() => {
                    closeMenu();
                    openEditModal(activeMenuEventType);
                  }}
                >
                  <ItemIconContainer>
                    <Pencil size={14} />
                  </ItemIconContainer>
                  <ItemLabel>Edit</ItemLabel>
                </MenuItem>
              )}
              {canDelete && (
                <MenuItem
                  type="button"
                  onClick={() => {
                    closeMenu();
                    setDeleteConfirmId(activeMenuEventType.id);
                  }}
                >
                  <ItemIconContainer>
                    <Trash2 size={14} />
                  </ItemIconContainer>
                  <ItemLabel>Delete</ItemLabel>
                </MenuItem>
              )}
            </DropdownMenu>
          </>,
          document.body
        )}

      {deleteConfirmId !== null && (
        <DeletePopup
          text="Are you sure you want to delete this event type?"
          buttonText="Delete"
          cancel={() => setDeleteConfirmId(null)}
          Delete={handleConfirmDelete}
          isDeleting={isDeleting}
        />
      )}

      <ListReportPrintTemplate
        printRef={reportPrintRef}
        title="Event Types Report"
        subtitle="Quotation event types overview"
        user={user ? { name: user.name, phone_number: user.phone_number, email: user.email } : undefined}
        columns={[
          { key: "name", label: "Event Type", width: "24%" },
          { key: "description", label: "Description", width: "34%" },
          {
            key: "price",
            label: "Price",
            align: "right",
            width: "16%",
            render: (value) => <CellBold>{String(value)}</CellBold>,
          },
          { key: "created_at", label: "Created On", width: "16%" },
        ]}
        rows={eventTypeReportRows}
        summaryItems={eventTypeReportSummary}
      />
    </ScrollPage>
  );
};

export default QuotationEventTypesPage;